import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Folder, FolderOpen, ChevronRight, ChevronDown, Loader2,
  RefreshCw, Star, Settings2, GripVertical, FileText,
  Mail, Paperclip, FolderUp
} from "lucide-react";

import { API } from "../config/api";

// ── Tree Node ────────────────────────────────────────────────────────
function FolderNode({
  folder, depth, driveId, headers, t,
  onSelectFolder, selectedFolderId,
  onDropEmail, onDropAttachment,
  expandedIds, onToggleExpand, childrenMap, loadingIds, filesMap
}) {
  const [dragOver, setDragOver] = useState(false);
  const isExpanded = expandedIds.has(folder.id);
  const isSelected = selectedFolderId === folder.id;
  const isLoading = loadingIds.has(folder.id);
  const children = childrenMap.get(folder.id) || [];
  const hasChildren = folder.childCount > 0;
  const nameLC = folder.name.toLowerCase();
  const isAttendances = nameLC.includes("attendan");
  const isDocuments = nameLC === "documents" || nameLC === "drafts";

  const handleClick = () => {
    onSelectFolder(folder, driveId);
    if (hasChildren) onToggleExpand(folder.id, driveId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    // Determine what's being dropped
    const dataType = e.dataTransfer.getData("text/plain");
    if (isAttendances) {
      onDropEmail(folder, driveId);
    } else {
      onDropAttachment(folder, driveId);
    }
  };

  const indent = depth * 16;
  const folderIcon = isExpanded ? (
    <FolderOpen className={`w-3.5 h-3.5 flex-shrink-0 ${isAttendances ? 'text-blue-400' : isDocuments ? 'text-status-amber' : 'text-teal/70'}`} />
  ) : (
    <Folder className={`w-3.5 h-3.5 flex-shrink-0 ${isAttendances ? 'text-blue-400' : isDocuments ? 'text-status-amber' : 'text-teal/70'}`} />
  );

  return (
    <>
      <div
        style={{ paddingLeft: `${indent + 4}px` }}
        className={`flex items-center gap-1.5 py-1 px-1 cursor-pointer text-[11px] rounded-sm transition-all select-none
          ${isSelected ? `${t.bgActive} ${t.textAccent}` : `${t.textSec} ${t.bgHover}`}
          ${dragOver ? 'ring-2 ring-inset ring-teal/60 bg-teal-dim/20' : ''}`}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid={`sp-folder-${folder.name.toLowerCase().replace(/\s+/g, '-')}`}
        title={`${folder.name}${isAttendances ? ' (drop email here)' : isDocuments ? ' (drop attachments here)' : ''}`}
      >
        {hasChildren ? (
          isExpanded ? <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-50" /> : <ChevronRight className="w-3 h-3 flex-shrink-0 opacity-50" />
        ) : (
          <span className="w-3 flex-shrink-0" />
        )}
        {folderIcon}
        <span className="truncate flex-1 font-medium">{folder.name}</span>
        {isLoading && <Loader2 className="w-3 h-3 animate-spin opacity-50 flex-shrink-0" />}
        {dragOver && isAttendances && <Mail className="w-3 h-3 text-blue-400 flex-shrink-0 animate-pulse" />}
        {dragOver && !isAttendances && <Paperclip className="w-3 h-3 text-status-amber flex-shrink-0 animate-pulse" />}
      </div>
      {isExpanded && children.length > 0 && children.map(child => (
        <FolderNode
          key={child.id}
          folder={child}
          depth={depth + 1}
          driveId={driveId}
          headers={headers}
          t={t}
          onSelectFolder={onSelectFolder}
          selectedFolderId={selectedFolderId}
          onDropEmail={onDropEmail}
          onDropAttachment={onDropAttachment}
          expandedIds={expandedIds}
          onToggleExpand={onToggleExpand}
          childrenMap={childrenMap}
          loadingIds={loadingIds}
          filesMap={filesMap}
        />
      ))}
      {/* File items in this folder */}
      {isExpanded && (filesMap?.get(folder.id) || []).map(file => {
        const isEmail = file.name?.endsWith('.eml') || file.name?.endsWith('.msg');
        const sizeKB = file.size ? Math.round(file.size / 1024) : 0;
        const sizeLabel = sizeKB > 1024 ? (sizeKB / 1024).toFixed(1) + ' MB' : sizeKB + ' KB';
        const modified = file.lastModified ? new Date(file.lastModified).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';
        return (
          <a
            key={file.id}
            href={file.webUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            style={{ paddingLeft: `${(depth + 1) * 16 + 4}px` }}
            className={`flex items-center gap-1.5 py-0.5 px-1 text-[10px] rounded-sm ${t.textMuted} hover:bg-gray-700/30 cursor-pointer group`}
            title={file.name}
            data-testid={`sp-file-${file.name?.replace(/\s+/g, '-')}`}
          >
            {isEmail
              ? <Mail className="w-3 h-3 text-blue-400/70 flex-shrink-0" />
              : <FileText className="w-3 h-3 text-gray-500 flex-shrink-0" />
            }
            <span className="truncate flex-1">{file.name}</span>
            <span className={`text-[9px] ${t.textDim} flex-shrink-0`}>{modified}</span>
            <span className={`text-[9px] ${t.textDim} flex-shrink-0 w-10 text-right`}>{sizeLabel}</span>
          </a>
        );
      })}
    </>
  );
}


// ── Main SharePoint Browser ──────────────────────────────────────────
export function SharePointBrowser({
  email, onFileToSharePoint, filingInProgress, t, headers,
  draggedMsgIds, checkedCount
}) {
  const [rootFolders, setRootFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driveId, setDriveId] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedDriveId, setSelectedDriveId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [childrenMap, setChildrenMap] = useState(new Map());
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [defaultPracticeArea, setDefaultPracticeArea] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [showAttachPrompt, setShowAttachPrompt] = useState(false);
  const [pendingFileTarget, setPendingFileTarget] = useState(null);
  const [subfolders, setSubfolders] = useState([]);
  const [attachTargetFolder, setAttachTargetFolder] = useState(null);

  const [filesMap, setFilesMap] = useState(new Map());

  // Load user preferences and initial folder tree
  useEffect(() => {
    if (!headers?.["X-Bonsai-User-Id"]) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Load preferences
        const prefsRes = await axios.get(`${API}/bonsai/mail/preferences`, { headers });
        if (cancelled) return;
        const pa = prefsRes.data?.default_practice_area;
        if (pa) setDefaultPracticeArea(pa);

        // Load SL folder (practice areas)
        const res = await axios.get(`${API}/bonsai/mail/sp-browse`, { params: { path: "SL" }, headers });
        if (cancelled) return;
        setRootFolders(res.data.folders || []);
        setDriveId(res.data.driveId);
        setBreadcrumb([{ name: "SL", path: "SL" }]);

        // Auto-expand default practice area
        if (pa && res.data.driveId) {
          const paFolder = (res.data.folders || []).find(f => f.name === pa.split("/").pop());
          if (paFolder) {
            await loadChildren(paFolder.id, res.data.driveId);
            if (!cancelled) setExpandedIds(new Set([paFolder.id]));
          }
        }
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status !== 401) {
          console.error("[SPBrowser] Failed to load SP folders:", e.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [headers?.["X-Bonsai-User-Id"]]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadChildren = useCallback(async (itemId, drv) => {
    const effectiveDrive = drv || driveId;
    if (!effectiveDrive || !itemId) return;

    setLoadingIds(prev => new Set([...prev, itemId]));
    try {
      const res = await axios.get(`${API}/bonsai/mail/sp-browse`, {
        params: { drive_id: effectiveDrive, item_id: itemId, include_files: true },
        headers
      });
      setChildrenMap(prev => {
        const next = new Map(prev);
        next.set(itemId, res.data.folders || []);
        return next;
      });
      // Store files for this folder
      if (res.data.files?.length > 0) {
        setFilesMap(prev => {
          const next = new Map(prev);
          next.set(itemId, res.data.files);
          return next;
        });
      }
    } catch {
      toast.error("Failed to load subfolders");
    } finally {
      setLoadingIds(prev => { const n = new Set(prev); n.delete(itemId); return n; });
    }
  }, [driveId, headers]);

  const handleToggleExpand = useCallback(async (folderId, drv) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
        // Load children if not already loaded
        if (!childrenMap.has(folderId)) {
          loadChildren(folderId, drv);
        }
      }
      return next;
    });
  }, [childrenMap, loadChildren]);

  const handleSelectFolder = useCallback((folder, drv) => {
    setSelectedFolder(folder);
    setSelectedDriveId(drv || driveId);
  }, [driveId]);

  const handleRefresh = async () => {
    setLoading(true);
    setChildrenMap(new Map());
    setExpandedIds(new Set());
    try {
      const res = await axios.get(`${API}/bonsai/mail/sp-browse`, { params: { path: "SL" }, headers });
      setRootFolders(res.data.folders || []);
      setDriveId(res.data.driveId);
    } catch {
      toast.error("Failed to refresh");
    } finally {
      setLoading(false);
    }
  };

  const setAsDefault = async (folderName) => {
    try {
      await axios.put(`${API}/bonsai/mail/preferences`, { default_practice_area: `SL/${folderName}` }, { headers });
      setDefaultPracticeArea(`SL/${folderName}`);
      toast.success(`Default practice area set to ${folderName}`);
    } catch {
      toast.error("Failed to save preference");
    }
  };

  // Filing handlers
  const handleDropEmail = useCallback((folder, drv) => {
    if (!email && draggedMsgIds?.length === 0) {
      toast.info("Select an email first");
      return;
    }
    // File email to this folder (typically Attendances)
    const effectiveDrive = drv || driveId;
    // Check if parent matter has subfolders loaded for attachment prompt
    const parentId = folder.parentPath?.split("/").pop();
    const siblings = childrenMap.get(parentId) || [];
    const docFolder = siblings.find(s => s.name.toLowerCase() === "documents" || s.name.toLowerCase() === "drafts");

    if (email?.attachments?.length > 0) {
      setPendingFileTarget({ driveId: effectiveDrive, folderId: folder.id, folderName: folder.name });
      setSubfolders(siblings);
      setAttachTargetFolder(docFolder || null);
      setShowAttachPrompt(true);
    } else {
      onFileToSharePoint({ driveId: effectiveDrive, folderId: folder.id, includeAttachments: false });
    }
  }, [email, driveId, childrenMap, draggedMsgIds, onFileToSharePoint]);

  const handleDropAttachment = useCallback((folder, drv) => {
    if (!email?.attachments?.length) {
      toast.info("No attachments to save");
      return;
    }
    const effectiveDrive = drv || driveId;
    onFileToSharePoint({
      driveId: effectiveDrive,
      folderId: folder.id,
      includeAttachments: true,
      attachmentDriveId: effectiveDrive,
      attachmentFolderId: folder.id
    });
  }, [email, driveId, onFileToSharePoint]);

  const confirmFileWithAttachments = (includeAtts, attFolder) => {
    setShowAttachPrompt(false);
    if (pendingFileTarget) {
      onFileToSharePoint({
        driveId: pendingFileTarget.driveId,
        folderId: pendingFileTarget.folderId,
        includeAttachments: includeAtts,
        attachmentDriveId: includeAtts && attFolder ? pendingFileTarget.driveId : undefined,
        attachmentFolderId: includeAtts && attFolder ? attFolder.id : undefined,
      });
    }
    setPendingFileTarget(null);
  };

  // Manual file button
  const handleFileSelected = () => {
    if (!selectedFolder || !selectedDriveId) {
      toast.error("Select a folder in the tree first");
      return;
    }
    const nameLC = selectedFolder.name.toLowerCase();
    const isAttendances = nameLC.includes("attendan");

    if (isAttendances && email?.attachments?.length > 0) {
      // Look for sibling document folders
      for (const [, children] of childrenMap) {
        if (children.some(c => c.id === selectedFolder.id)) {
          const docFolder = children.find(s => s.name.toLowerCase() === "documents" || s.name.toLowerCase() === "drafts");
          setPendingFileTarget({ driveId: selectedDriveId, folderId: selectedFolder.id });
          setSubfolders(children);
          setAttachTargetFolder(docFolder || null);
          setShowAttachPrompt(true);
          return;
        }
      }
    }
    onFileToSharePoint({ driveId: selectedDriveId, folderId: selectedFolder.id, includeAttachments: false });
  };

  return (
    <div className={`w-72 flex-shrink-0 border-l ${t.border} ${t.bgFiling} overflow-hidden flex flex-col`} data-testid="sp-browser-panel">
      {/* Header */}
      <div className={`px-3 py-2 border-b ${t.border} flex items-center gap-2 flex-shrink-0`}>
        <div className={`w-6 h-6 ${t.bgActive} rounded flex items-center justify-center`}>
          <FolderUp className={`w-3.5 h-3.5 ${t.textAccentDim}`} />
        </div>
        <span className={`text-xs font-semibold ${t.text} flex-1`}>Client Folders</span>
        <button onClick={handleRefresh} className={`p-1 rounded ${t.ribbonHover}`} title="Refresh" data-testid="sp-refresh-btn">
          <RefreshCw className={`w-3 h-3 ${t.textDim} ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Breadcrumb */}
      {breadcrumb.length > 0 && (
        <div className={`px-3 py-1.5 border-b ${t.border} flex items-center gap-1 text-[10px] ${t.textDim} flex-shrink-0`}>
          {breadcrumb.map((bc, i) => (
            <React.Fragment key={bc.path}>
              {i > 0 && <ChevronRight className="w-2.5 h-2.5 opacity-50" />}
              <span className={i === breadcrumb.length - 1 ? t.textAccentDim : ''}>{bc.name}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Email info bar */}
      {email && (
        <div className={`px-3 py-2 border-b ${t.border} flex-shrink-0`}>
          <div className="flex items-start gap-2">
            <Mail className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className={`text-[10px] font-medium ${t.text} truncate`}>{email.subject}</p>
              {email.attachments?.length > 0 && (
                <div className="flex items-center gap-1 mt-0.5">
                  <Paperclip className="w-2.5 h-2.5 text-status-amber" />
                  <span className={`text-[9px] text-status-amber`}>{email.attachments.length} attachment{email.attachments.length > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tree view */}
      <div className="flex-1 overflow-y-auto py-1 px-1" data-testid="sp-tree-view">
        {loading ? (
          <div className={`flex items-center justify-center gap-2 py-8 ${t.textDim} text-xs`}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading folders...
          </div>
        ) : rootFolders.length === 0 ? (
          <div className={`text-center py-8 ${t.textDim} text-xs`}>No folders found</div>
        ) : (
          rootFolders.map(folder => (
            <React.Fragment key={folder.id}>
              <FolderNode
                folder={folder}
                depth={0}
                driveId={driveId}
                headers={headers}
                t={t}
                onSelectFolder={handleSelectFolder}
                selectedFolderId={selectedFolder?.id}
                onDropEmail={handleDropEmail}
                onDropAttachment={handleDropAttachment}
                expandedIds={expandedIds}
                onToggleExpand={handleToggleExpand}
                childrenMap={childrenMap}
                loadingIds={loadingIds}
                filesMap={filesMap}
              />
              {/* Default practice area star */}
              {defaultPracticeArea === `SL/${folder.name}` && (
                <div className="pl-8 py-0.5">
                  <span className="text-[9px] text-status-amber flex items-center gap-1">
                    <Star className="w-2.5 h-2.5 fill-amber-400" /> Default
                  </span>
                </div>
              )}
            </React.Fragment>
          ))
        )}
      </div>

      {/* File button */}
      <div className={`px-3 py-2 border-t ${t.border} flex-shrink-0 space-y-2`}>
        <Button onClick={handleFileSelected}
          disabled={!selectedFolder || filingInProgress}
          className="w-full bg-teal hover:bg-teal text-white text-xs h-8 font-medium"
          data-testid="file-selected-btn">
          {filingInProgress ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <FolderUp className="w-3.5 h-3.5 mr-1.5" />}
          {selectedFolder ? `File to ${selectedFolder.name}` : "Select a folder"}
        </Button>
        {/* Set default PA */}
        {selectedFolder && rootFolders.some(f => f.id === selectedFolder.id) && (
          <button
            onClick={() => setAsDefault(selectedFolder.name)}
            className={`w-full text-center text-[10px] ${t.textDim} hover:${t.textAccent} py-1 transition-colors`}
            data-testid="set-default-pa-btn">
            <Star className="w-3 h-3 inline mr-1" />
            Set as default practice area
          </button>
        )}
      </div>

      {/* Attachment confirmation dialog */}
      {showAttachPrompt && email?.attachments?.length > 0 && (
        <div className={`px-3 py-3 border-t ${t.border} flex-shrink-0 ${t.bgInput} space-y-2`} data-testid="attach-prompt-dialog">
          <div className="flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-status-amber" />
            <span className={`text-xs font-semibold ${t.text}`}>Save attachments?</span>
          </div>
          <p className={`text-[10px] ${t.textSec}`}>
            This email has <span className="font-semibold text-status-amber">{email.attachments.length}</span> attachment{email.attachments.length > 1 ? 's' : ''}. Where should they go?
          </p>
          <div className="flex flex-wrap gap-1">
            {subfolders.filter(sf => !sf.name.toLowerCase().includes("attendan")).map(sf => (
              <button key={sf.id} onClick={() => setAttachTargetFolder(sf)}
                className={`px-2 py-1 rounded text-[10px] font-medium transition-colors border
                  ${attachTargetFolder?.id === sf.id ? "bg-status-amber-dim/40 text-status-amber border-amber-700/50" : `${t.bgInput} ${t.textMuted} ${t.border} ${t.bgHover}`}`}
                data-testid={`prompt-att-${sf.name.toLowerCase().replace(/\s/g, '-')}`}>{sf.name}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => confirmFileWithAttachments(true, attachTargetFolder)}
              disabled={!attachTargetFolder}
              className="flex-1 bg-status-amber hover:bg-status-amber text-white text-[10px] h-7"
              data-testid="confirm-save-attachments-btn">
              Save to {attachTargetFolder?.name || "..."}
            </Button>
            <Button size="sm" variant="outline" onClick={() => confirmFileWithAttachments(false, null)}
              className={`flex-1 text-[10px] h-7 ${t.border} ${t.textSec}`}
              data-testid="skip-attachments-btn">
              Email only
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
